/**
 * 前端 API 服务层
 *
 * 封装 axios 实例和所有 API 调用
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getCookie } from '../utils/cookie';
import { getConfiguredApiBaseUrl } from './runtimeApiConfig';

type RetriableRequestConfig = InternalAxiosRequestConfig & {
    _retry?: boolean;
};

const shouldRedirectToLogin = () => window.location.pathname !== '/login';

const redirectToLogin = () => {
    if (shouldRedirectToLogin()) {
        window.location.href = '/login';
    }
};

const isAuthBoundaryRequest = (url?: string) =>
    ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout', '/auth/logout-all'].some(
        (path) => url?.includes(path) ?? false
    );

// 创建 axios 实例
const apiClient = axios.create({
    timeout: 30000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// 请求拦截器：注入匿名 ID
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        config.baseURL = getConfiguredApiBaseUrl();

        // 1. 始终尝试注入匿名 ID（用于聊天界面的会话历史）
        const anonymousId = getCookie('anonymous_id');
        if (anonymousId && config.headers) {
            config.headers['X-Anonymous-ID'] = anonymousId;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// 响应拦截器：处理 401 并尝试刷新 Cookie 会话
apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as RetriableRequestConfig | undefined;

        if (!originalRequest || error.response?.status !== 401) {
            return Promise.reject(error);
        }

        if (!originalRequest._retry && !isAuthBoundaryRequest(originalRequest.url)) {
            originalRequest._retry = true;

            try {
                await apiClient.post('/auth/refresh');
                return apiClient.request(originalRequest);
            } catch (refreshError) {
                redirectToLogin();
                return Promise.reject(refreshError);
            }
        }

        if (!isAuthBoundaryRequest(originalRequest.url)) {
            redirectToLogin();
        }
        return Promise.reject(error);
    }
);

// ===== 认证 API =====

export const authApi = {
    login: async (username: string, password: string) => {
        const res = await apiClient.post('/auth/login', { username, password });
        return res.data;
    },

    refresh: async () => {
        const res = await apiClient.post('/auth/refresh');
        return res.data;
    },

    register: async (username: string, password: string) => {
        const res = await apiClient.post('/auth/register', { username, password });
        return res.data;
    },

    me: async () => {
        const res = await apiClient.get('/auth/me');
        return res.data;
    },

    changePassword: async (data: { old_password: string; new_password: string }) => {
        const res = await apiClient.post('/auth/change-password', data);
        return res.data;
    },

    logout: async () => {
        try {
            await apiClient.post('/auth/logout');
        } catch {
            // 即使 logout 失败也交给服务端 Cookie 失效和后续鉴权处理
        }
    },
};

// ===== 对话 API =====

import type {
    Conversation,
    AdminConversationListParams,
    AdminConversationListResponse,
    AdminConversationDetail,
} from './types';

export const chatApi = {
    /** 获取会话历史 */
    getHistory: async (conversationId: string): Promise<Conversation> => {
        const res = await apiClient.get(`/chat/history/${conversationId}`);
        return res.data.data;
    },

    /** 获取会话列表 */
    listConversations: async (page: number = 1, page_size: number = 50) => {
        const res = await apiClient.get('/chat/conversations', { params: { page, page_size } });
        return res.data.data;
    },

    /** 删除会话 */
    deleteConversation: async (conversationId: string): Promise<void> => {
        const res = await apiClient.delete(`/chat/history/${conversationId}`);
        return res.data;
    },

    /** 获取会话历史消息 */
    getConversationMessages: async (conversationId: string) => {
        const res = await apiClient.get(`/chat/history/${conversationId}`);
        return res.data.data.messages || [];
    },
};

// ===== 管理后台 API - 会话管理 =====

export const conversationApi = {
    /** 获取会话列表（管理端） */
    list: async (params?: AdminConversationListParams): Promise<AdminConversationListResponse> => {
        const res = await apiClient.get('/admin/conversations', { params });
        return res.data.data;
    },

    /** 获取会话详情（管理端） */
    get: async (sessionId: string): Promise<AdminConversationDetail> => {
        const res = await apiClient.get(`/admin/conversations/${sessionId}`);
        return res.data.data;
    },

    /** 归档会话 */
    archive: async (sessionId: string): Promise<void> => {
        const res = await apiClient.put(`/admin/conversations/${sessionId}/archive`);
        return res.data;
    },
};

// ===== 知识库 API =====

export interface ListDocumentsParams {
    page?: number;
    page_size?: number;
    category?: string;
    status_filter?: string;
    keyword?: string;
}

// knowledgeApi 定义在文件末尾（包含完整的异步索引支持）
export const faqApi = {
    /** 获取 FAQ 列表 */
    list: async (params?: { skip?: number; limit?: number; keyword?: string; category?: string; is_active?: boolean }) => {
        const res = await apiClient.get('/faqs', { params });
        return res.data;
    },

    /** 创建 FAQ */
    create: async (data: { question: string; answer: string; category?: string; tags?: string; is_active?: boolean }) => {
        const res = await apiClient.post('/faqs', data);
        return res.data;
    },

    /** 获取 FAQ 详情 */
    get: async (id: number) => {
        const res = await apiClient.get(`/faqs/${id}`);
        return res.data;
    },

    /** 更新 FAQ */
    update: async (id: number, data: { question?: string; answer?: string; category?: string; tags?: string; is_active?: boolean }) => {
        const res = await apiClient.put(`/faqs/${id}`, data);
        return res.data;
    },

    /** 删除 FAQ */
    delete: async (id: number) => {
        const res = await apiClient.delete(`/faqs/${id}`);
        return res.data;
    },

    /** 下载导入模板（返回 Blob） */
    downloadTemplate: async () => {
        const res = await apiClient.get('/faqs/template/download', { responseType: 'blob' });
        return res.data as Blob;
    },

    /** Excel 批量导入 */
    importExcel: async (file: File) => {
        const form = new FormData();
        form.append('file', file);
        const res = await apiClient.post('/faqs/import', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return res.data;
    },

    /** 获取公共置顶 FAQ（匿名可访问） */
    getPublicPinned: async () => {
        const res = await apiClient.get('/faqs/public/pinned');
        return res.data;
    },

    /** 获取置顶 FAQ 列表（管理员） */
    getPinned: async () => {
        const res = await apiClient.get('/faqs/pinned');
        return res.data;
    },

    /** 设置 FAQ 置顶 */
    pin: async (id: number, pinOrder: number) => {
        const res = await apiClient.patch(`/faqs/${id}/pin`, { pin_order: pinOrder });
        return res.data;
    },

    /** 批量更新置顶顺序 */
    batchUpdatePinned: async (pins: Array<{ id: number; pin_order: number }>) => {
        const res = await apiClient.post('/faqs/pinned/batch', { pins });
        return res.data;
    },

    /** 取消置顶 */
    unpin: async (id: number) => {
        const res = await apiClient.patch(`/faqs/${id}/pin`, { pin_order: 0 });
        return res.data;
    },

    /** 获取置顶配置 */
    getPinnedConfig: async () => {
        const res = await apiClient.get('/faqs/pinned/config');
        return res.data;
    },
};

export const userApi = {
    list: async (params?: { skip?: number; limit?: number; keyword?: string; role?: string }) => {
        const res = await apiClient.get('/users', { params });
        return res.data;
    },
    create: async (data: { username: string; email: string; password: string; role?: string }) => {
        const res = await apiClient.post('/users', data);
        return res.data;
    },
    get: async (id: string) => {
        const res = await apiClient.get(`/users/${id}`);
        return res.data;
    },
    update: async (id: string, data: { username?: string; email?: string; role?: string; is_active?: boolean; password?: string }) => {
        const res = await apiClient.put(`/users/${id}`, data);
        return res.data;
    },
    delete: async (id: string) => {
        const res = await apiClient.delete(`/users/${id}`);
        return res.data;
    },
    toggleActive: async (id: string) => {
        const res = await apiClient.put(`/users/${id}/toggle-active`);
        return res.data;
    },
};

export const modelApi = {
    list: async (params?: { skip?: number; limit?: number; model_type?: string; keyword?: string }) => {
        const res = await apiClient.get('/models', { params });
        return res.data;
    },
    create: async (data: Record<string, any>) => {
        const res = await apiClient.post('/models', data);
        return res.data;
    },
    get: async (id: number) => {
        const res = await apiClient.get(`/models/${id}`);
        return res.data;
    },
    update: async (id: number, data: Record<string, any>) => {
        const res = await apiClient.put(`/models/${id}`, data);
        return res.data;
    },
    delete: async (id: number) => {
        const res = await apiClient.delete(`/models/${id}`);
        return res.data;
    },
    test: async (id: number) => {
        const res = await apiClient.post(`/models/${id}/test`);
        return res.data;
    },
};

// ===== 切片配置类型 =====

export interface ChunkConfig {
    /** 切片大小（字符数） */
    chunk_size: number;
    /** 切片重叠（字符数） */
    chunk_overlap: number;
    /** 是否自动计算 */
    auto_calculated?: boolean;
}

// ===== 索引任务 API =====

export interface IndexingTask {
    id: number;
    doc_id: number;
    status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
    progress: number;
    total_chunks?: number;
    processed_chunks?: number;
    current_stage?: 'extracting' | 'embedding' | 'storing';
    chunk_size?: number;
    chunk_overlap?: number;
    retry_count: number;
    max_retries: number;
    created_at: string;
    started_at?: string;
    completed_at?: string;
    estimated_remaining?: string;
    error_message?: string;
}

export interface UploadDocumentParams {
    title: string;
    category?: string;
    tags?: string;
    file: File;
    chunk_size?: number;
    chunk_overlap?: number;
}

export interface UploadDocumentResponse {
    id: number;
    title: string;
    status: string;
    task_id: number;
    file_info: {
        name: string;
        size: number;
        type: string;
    };
    chunk_config: {
        auto_calculated: boolean;
        chunk_size: number;
        chunk_overlap: number;
    };
}

export const taskApi = {
    /** 获取任务进度 */
    get: async (taskId: number): Promise<IndexingTask> => {
        const res = await apiClient.get(`/knowledge/tasks/${taskId}`);
        return res.data.data;
    },

    /** 获取文档的所有任务 */
    listByDoc: async (docId: number): Promise<{ tasks: Partial<IndexingTask>[]; total: number }> => {
        const res = await apiClient.get(`/knowledge/documents/${docId}/tasks`);
        return res.data.data;
    },

    /** 取消任务 */
    cancel: async (taskId: number): Promise<{ id: number; status: string }> => {
        const res = await apiClient.post(`/knowledge/tasks/${taskId}/cancel`);
        return res.data.data;
    },

    /** 重试任务 */
    retry: async (taskId: number): Promise<{ id: number; status: string; retry_count: number }> => {
        const res = await apiClient.post(`/knowledge/tasks/${taskId}/retry`);
        return res.data.data;
    },
};

// 扩展 knowledgeApi 添加上传方法
export const knowledgeApi = {
    /** 获取文档列表 */
    list: async (params: ListDocumentsParams = {}) => {
        const res = await apiClient.get('/knowledge/documents', { params });
        return res.data;
    },

    /** 获取单个文档 */
    get: async (id: number) => {
        const res = await apiClient.get(`/knowledge/documents/${id}`);
        return res.data;
    },

    /** 上传文档（multipart/form-data） */
    upload: async (formData: FormData) => {
        const res = await apiClient.post('/knowledge/documents', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return res.data;
    },

    /** 上传文档（带切片配置） */
    uploadWithConfig: async (params: UploadDocumentParams): Promise<UploadDocumentResponse> => {
        const formData = new FormData();
        formData.append('title', params.title);
        if (params.category) formData.append('category', params.category);
        if (params.tags) formData.append('tags', params.tags);
        formData.append('file', params.file);
        if (params.chunk_size) formData.append('chunk_size', String(params.chunk_size));
        formData.append('chunk_overlap', String(params.chunk_overlap || 50));

        const res = await apiClient.post('/knowledge/documents', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return res.data.data;
    },

    /** 创建文档（兼容 JSON 模式，仅用于编辑） */
    create: async (data: { title: string; content?: string; category?: string; tags?: string[] }) => {
        const res = await apiClient.post('/knowledge/documents', data);
        return res.data;
    },

    /** 更新文档 */
    update: async (id: number, data: { title?: string; content?: string; category?: string; tags?: string[]; status?: string }) => {
        const res = await apiClient.put(`/knowledge/documents/${id}`, data);
        return res.data;
    },

    /** 删除文档 */
    delete: async (id: number) => {
        const res = await apiClient.delete(`/knowledge/documents/${id}`);
        return res.data;
    },

    /** 发布文档 */
    publish: async (id: number) => {
        const res = await apiClient.post(`/knowledge/documents/${id}/publish`);
        return res.data;
    },

    /** 归档文档 */
    archive: async (id: number) => {
        const res = await apiClient.post(`/knowledge/documents/${id}/archive`);
        return res.data;
    },

    /** 搜索文档 */
    search: async (q: string, top_k: number = 5, category?: string) => {
        const res = await apiClient.get('/knowledge/search', {
            params: { q, top_k, category },
        });
        return res.data;
    },

    /** 获取分类列表 */
    categories: async () => {
        const res = await apiClient.get('/knowledge/categories');
        return res.data;
    },
};

export default apiClient;

// ===== Dashboard API =====

export interface BasicStats {
    total_conversations: number;
    conversation_trend: string;
    ai_resolve_rate: number;
    resolve_trend: string;
    avg_response_time: number;
    response_trend: string;
    failed_tasks: number;
}

export interface IntentDistribution {
    name: string;
    value: number;
    color: string;
}

export interface RecentConversation {
    id: string;
    user_name: string;
    intent: string;
    message: string;
    time: string;
    avatar_color: string;
}

export interface DashboardStatsResponse {
    basic_stats: BasicStats;
    intent_distribution: IntentDistribution[];
    recent_conversations: RecentConversation[];
}

export interface AnalyticsStats {
    knowledge_docs: number;
    knowledge_trend: string;
    faq_count: number;
    faq_trend: string;
    today_users: number;
    users_trend: string;
    today_conversations: number;
    conversations_trend: string;
}

export interface ConversationTrend {
    date: string;
    count: number;
}

export interface KnowledgeStatus {
    indexed: number;
    processing: number;
    failed: number;
}

export interface DashboardAnalyticsResponse {
    analytics_stats: AnalyticsStats;
    conversation_trend: ConversationTrend[];
    knowledge_status: KnowledgeStatus;
}

export const dashboardApi = {
    /** 获取基础统计数据 */
    getStats: async (): Promise<DashboardStatsResponse> => {
        const res = await apiClient.get('/admin/dashboard/stats');
        return res.data.data;
    },

    /** 获取运营分析数据 */
    getAnalytics: async (): Promise<DashboardAnalyticsResponse> => {
        const res = await apiClient.get('/admin/dashboard/analytics');
        return res.data.data;
    },
};
